import React, { useState } from "react"
import { Box, Collapse, Typography } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ExpandLessIcon from "@mui/icons-material/ExpandLess"
import NavigationIcon from "@mui/icons-material/Navigation"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { ConvertDDToDM, normaliseLng } from "@/utils/geoFormat"
import { KPI_TILE_BG, PANEL_DIVIDER, PANEL_TEXT_MUTED, WW_BLUE } from "@/theme/tokens"
import { PANEL_SUBTITLE_SX, PLAN_INPUT_FONT_SIZE } from "@/theme/panelStyles"
import KpiTile, { KPI_LABEL_SX } from "@/components/ui/KpiTile"

dayjs.extend(utc)

// Body frame shared by the waypoint and vessel cards.
export const INFO_CARD_BODY_SX = { p: "12px 14px 8px", display: "flex", flexDirection: "column", gap: "10px" }

export const finite = (v) => (v != null && v !== "" && Number.isFinite(Number(v)) ? Number(v) : null)

export const tileRow = (columns) => ({ display: "grid", gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: "6px" })

// Time (large) and date on the left, position on the right.
export function CardHero({ label, timestamp, coords, emptyText = "Not calculated", footnote = null }) {
  const [lat, lon] = coords ? ConvertDDToDM([normaliseLng(coords[0]), coords[1]]) : []
  const t = timestamp ? dayjs.utc(timestamp) : null
  return (
    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
      <Box>
        <Typography sx={KPI_LABEL_SX}>{label}</Typography>
        {t ? (
          <>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography sx={{ fontSize: 20, fontWeight: 700, color: WW_BLUE, lineHeight: 1.2 }}>
                {t.format("HH:mm")}
              </Typography>
              <Typography sx={PANEL_SUBTITLE_SX}>UTC</Typography>
            </Box>
            <Typography sx={{ fontSize: PLAN_INPUT_FONT_SIZE, fontWeight: 700, color: WW_BLUE }}>{t.format("ddd DD MMM YYYY")}</Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: PLAN_INPUT_FONT_SIZE, fontWeight: 600, color: PANEL_TEXT_MUTED, mt: 0.25 }}>{emptyText}</Typography>
        )}
        {footnote && <Typography sx={{ ...KPI_LABEL_SX, mt: 0.25 }}>{footnote}</Typography>}
      </Box>
      {lat && (
        <Box sx={{ textAlign: "right", pt: "14px" }}>
          {[lat, lon].map((line) => (
            <Typography key={line} sx={{ fontSize: 11, fontWeight: 600, color: WW_BLUE, fontVariantNumeric: "tabular-nums" }}>
              {line}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  )
}

// Same shape and colour as the vessel icon on the map.
export function CourseArrow({ deg, color = WW_BLUE }) {
  return (
    <Box
      sx={{
        width: 24,
        height: 24,
        my: "-5px",
        flex: "none",
        borderRadius: "50%",
        background: "white",
        border: `1.5px solid ${PANEL_DIVIDER}`,
        boxSizing: "border-box",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <NavigationIcon sx={{ fontSize: 15, color, transform: `rotate(${deg}deg)` }} />
    </Box>
  )
}

const degrees = (v) => (v == null ? null : `${Math.round(((v % 360) + 360) % 360)}°`)

export function CourseTile({ label, deg, color }) {
  return (
    <KpiTile label={label} value={degrees(deg)} adornment={deg != null ? <CourseArrow deg={deg} color={color} /> : null} />
  )
}

export function DetailsToggle({ children }) {
  const [expanded, setExpanded] = useState(false)
  return (
    <Box>
      <Box
        onClick={() => setExpanded((prev) => !prev)}
        sx={{
          py: 0.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          color: WW_BLUE,
          borderRadius: "8px",
          cursor: "pointer",
          userSelect: "none",
          "&:hover": { backgroundColor: KPI_TILE_BG },
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{expanded ? "Hide details" : "Show details"}</Typography>
        {expanded ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
      </Box>
      <Collapse in={expanded}>
        <Box sx={{ mt: 0.5, pt: 0.75, borderTop: `1px solid ${PANEL_DIVIDER}` }}>{children}</Box>
      </Collapse>
    </Box>
  )
}
